<template>
    <div class="row">
        <PreLoader :show="loading" />

        <div class="col-12 col-md-6 my-4">
            <AutocompleteField
                name="industry"
                :validationRules="{
                    required: true,
                }"
                :apiResultHandler="apiResultHandler"
                :errorMessage="$t('form.fields.employmentIndustry.errorMessage')"
                :label="$t('form.fields.employmentIndustry.label')"
                :limit="15"
                :optionsUrl="industry.optionsUrl"
                :defaultOption="['title', 'Other']"
                :appendDefaultOption="true"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <DateField
                ageMin="18"
                name="dateOfBirth"
                :errorMessage="$t('form.fields.dateOfBirth.errorMessage')"
                :label="$t('form.fields.dateOfBirth.label')"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <InputField
                name="emailAddress"
                :validationRules="{
                    required: true,
                    loqateEmail: true,
                }"
                :errorMessage="$t('form.fields.emailAddress.errorMessage')"
                :label="$t('form.fields.emailAddress.label')"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <InputField
                name="mobileNumber"
                :validationRules="{
                    required: true,
                    loqateMobilePhone: true,
                }"
                :errorMessage="$t('form.fields.mobileNumber.errorMessage')"
                :image="asset(`/img/flags/uk.svg`)"
                :label="$t('form.fields.mobileNumber.label')"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <ButtonSelectField
                label="Loan Term"
                name="loanTerm"
                :validationRules="{
                    required: true,
                }"
                :multiple="true"
                :options="loanTermOptions"
                buttonClasses="wizard-button"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <SelectField
                icon="far fa-calendar-alt"
                label="Loan Purpose"
                name="loanPurpose"
                :validationRules="{
                    required: true,
                }"
                :errorMessage="$t('form.fields.loanPurpose.errorMessage')"
                :options="loanPurposeOptions"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <AddressField
                :label="address.label"
                name="currentAddress"
                :errorMessage="address.errorMessage"
                :address-fields="address.fields"
            />
        </div>

        <div class="col-12 col-md-6 my-4">
            <ButtonSelectField
                label="Finances Bank"
                name="financesBank"
                :validationRules="{
                    required: true,
                }"
                :multiple="false"
                :options="financesBankOptions"
                buttonClasses="wizard-button"
            />
        </div>
    </div>
</template>

<script>
import { useRoute } from 'vue-router'

// lodash components
import map from 'lodash/map'

export default {
    created() {
        if (this.route.query.loader) {
            this.loading = true

            setTimeout(() => {
                this.loading = false
            }, this.route.query.duration || 10000)
        }
    },
    data() {
        return {
            route: useRoute(),
            loading: false,
            address: {
                label: 'Address',
                errorMessage: `${this.$t('form.fields.postcode.errorMessage')}`,
                fields: [
                    {
                        name: 'currentAddressPostcode',
                        label: `${this.$t('form.fields.address.labels.postcode')}`,
                        icon: null,
                        validationRules: 'required',
                        errorMessage: `${this.$t('form.fields.postcode.errorMessage')}`,
                    },
                    {
                        name: 'currentAddressBuildingNameOrNumber',
                        label: `${this.$t('form.fields.buildingNameOrNumber.label')}`,
                        icon: null,
                        validationRules: 'required',
                        errorMessage: `${this.$t('form.fields.buildingNameOrNumber.errorMessage')}`,
                    },
                    {
                        name: 'currentAddressStreetName',
                        label: `${this.$t('form.fields.streetName.label')}`,
                        icon: null,
                        validationRules: 'required',
                        errorMessage: `${this.$t('form.fields.streetName.errorMessage')}`,
                    },
                    {
                        name: 'currentAddressCity',
                        label: `${this.$t('form.fields.city.label')}`,
                        icon: null,
                        validationRules: 'required',
                        errorMessage: `${this.$t('form.fields.city.errorMessage')}`,
                    },
                    {
                        name: 'currentAddressCounty',
                        label: `${this.$t('form.fields.county.label')}`,
                        icon: null,
                        validationRules: null,
                        errorMessage: `${this.$t('form.fields.county.errorMessage')}`,
                    },
                ],
            },
            industry: {
                optionsUrl: this.url('/api/abi/industries'),
                options: [
                    {
                        id: 1,
                        title: 'Accountants',
                    },
                    {
                        id: 2,
                        title: 'Advertising/Public Relations',
                    },
                    {
                        id: 3,
                        title: 'Aerospace, Defense Contractors',
                    },
                    {
                        id: 4,
                        title: 'Food Processing & Sales',
                    },
                    {
                        id: 5,
                        title: 'Food Products Manufacturing',
                    },
                    {
                        id: 6,
                        title: 'Funeral Services',
                    },
                    {
                        id: 7,
                        title: 'Manufacturing, Misc',
                    },
                    {
                        id: 8,
                        title: 'Publishing & Printing',
                    },
                    {
                        id: 9,
                        title: 'Trash Collection/Waste Management',
                    },
                ],
            },
            loanPurposeOptions: [
                {
                    value: 'bills',
                    name: 'Bills',
                },
                {
                    value: 'debt_consolidation',
                    name: 'Debt consolidation',
                },
                {
                    value: 'personal_expense',
                    name: 'Personal expense',
                },
                {
                    value: 'special_occasion',
                    name: 'Special occasion',
                },
                {
                    value: 'holiday',
                    name: 'Holiday',
                },
                {
                    value: 'home_improvements',
                    name: 'Home improvements',
                },
                {
                    value: 'household_goods',
                    name: 'Household goods',
                },
                {
                    value: 'medical',
                    name: 'Medical',
                },
                {
                    value: 'one_off_payment',
                    name: 'One off payment',
                },
                {
                    value: 'other',
                    name: 'Other',
                },
            ],
            loanTermOptions: [
                {
                    value: 6,
                    name: '6 months',
                },
                {
                    value: 12,
                    name: '1 years',
                },
                {
                    value: 24,
                    name: '2 years',
                },
                {
                    value: 36,
                    name: '3 years',
                },
                {
                    value: 48,
                    name: '4 years',
                },
                {
                    value: 60,
                    name: '5+ years',
                },
            ],
            financesBankOptions: [
                {
                    value: 'barclays',
                    name: 'Barclays',
                    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/de/ABSA_Group_Limited_Logo.svg/1200px-ABSA_Group_Limited_Logo.svg.png',
                },
                {
                    value: 'rbs',
                    name: 'RBS',
                    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/de/ABSA_Group_Limited_Logo.svg/1200px-ABSA_Group_Limited_Logo.svg.png',
                },
                {
                    value: 'hsbc',
                    name: 'HSBC',
                    logo: 'https://w7.pngwing.com/pngs/812/28/png-transparent-pune-logo-barclays-bank-business-bank-blue-text-service-thumbnail.png',
                },
                {
                    value: 'monzo',
                    name: 'Monzo',
                    logo: 'https://w7.pngwing.com/pngs/812/28/png-transparent-pune-logo-barclays-bank-business-bank-blue-text-service-thumbnail.png',
                },
                {
                    value: 'starling',
                    name: 'Starling',
                    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/de/ABSA_Group_Limited_Logo.svg/1200px-ABSA_Group_Limited_Logo.svg.png',
                },
                {
                    value: 'nationwide',
                    name: 'Nationwide',
                    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/de/ABSA_Group_Limited_Logo.svg/1200px-ABSA_Group_Limited_Logo.svg.png',
                },
                {
                    value: 'tsb',
                    name: 'TSB',
                    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/de/ABSA_Group_Limited_Logo.svg/1200px-ABSA_Group_Limited_Logo.svg.png',
                },
                {
                    value: 'other',
                    name: 'Other',
                    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/de/ABSA_Group_Limited_Logo.svg/1200px-ABSA_Group_Limited_Logo.svg.png',
                },
            ],
        }
    },
    methods: {
        apiResultHandler: function (items) {
            return map(items, item => {
                return {
                    id: item.code || item.id,
                    title: item.name,
                }
            })
        },
    },
}
</script>
